import {pickAll, pipe, mergeAll, flatten, propEq} from 'ramda'
import {isPrivateDeal} from '@daedalus/core/src/offer/business/privateDeals'

import getHotelPagesSearchId from 'components/data/URLParams/mappers/getHotelPagesSearchId'
import {
  getTotalAtProperty,
  getHotelFeesResortFee,
  getHotelFeesTax,
  getHotelFeesOther,
  shouldAnchorPriceBeVisible,
  shouldComparisonOfferAnchorPriceBeVisible,
  getTotalSaving,
  getNightlyDisplayTotalPrice,
  getChargeableCurrencyPrice
} from 'business/price'
import {getHotelNightlyFees} from 'modules/rooms/selectors'
import {getAnchorPriceNightlyTotal} from 'modules/raaOffers/selectors'
import renamePropName from 'utils/renamePropName'

import {
  EventSpace,
  TrackEventNewType
} from 'modules/analytics/actions/trackEventNew'

import {
  ROOM_SELECTION_PAGE_PATH,
  PAYMENT_PAGE_PATH,
  CONFIRMATION_PAGE_PATH
} from 'routes'

import {getSavingPercentage} from '@daedalus/core/src/offer/business/price'
import {UrlCheckoutParamsType} from '@daedalus/core/src/utils/url/types/UrlParams'
import {
  RaaAnchorPriceBreakdownType,
  RaaOfferWithPrices
} from '@daedalus/core/src/offer/types/Raa'
import {Offer} from '@daedalus/core/src/offer/types/offer'
import {
  OfferContextType,
  OffersListContextType,
  ComparisonOfferContextType,
  ComparisonOffersListContextType,
  PriceContextType
} from 'types/trackingContext'
import {Room} from '@daedalus/core/src/room/types/room'
import {RoomConfigurationType} from 'types/roomConfiguration'
import {getCancellationDeadline} from '@daedalus/core/src/offer/business/cancellationPenalties'
import {
  getDisplayPrice,
  getTotalPrice,
  getTotalUpFrontPrice,
  isThereACurrencyConversion
} from '@daedalus/core/src/price/business/price'

type GetOfferContextPropsType = {
  roomName: string
  offer: Offer
  roomPosition: number
  offerPosition: number
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
  roomId: string
  cheapestOffer: Offer
  numberOfImages?: number
  roomDescription?: string
  roomAmenities?: string[]
}

type GetOffersListContextPropsType = {
  rooms: Room[]
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
}

type GetComparisonOfferContextPropsType = {
  raaOffer: RaaOfferWithPrices
  offerPosition: number
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
}

type GetComparisonOfferListContextPropsType = {
  raaOffers: RaaOfferWithPrices[]
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
}

type GetPayOffersListContextPropsType = {
  room: Room
  offers: Offer[]
  roomPosition: number
  offerPosition: number
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
  cheapestOffer: Offer
}

type GetPriceContextPropsType = {
  offer: Offer
  raaAnchorPriceBreakdown?: RaaAnchorPriceBreakdownType
  roomsConfig: RoomConfigurationType
  cheapestOffer: Offer
}

export const getMetadata = (urlParams: UrlCheckoutParamsType) => {
  const hotelPagesSearchId = getHotelPagesSearchId(urlParams)
  const searchId = '' // TODO: add this once RAA pass it to us ch20357
  const userId = '' // TODO: this should be id of the signed in user
  const viewPortHeight = window.innerHeight
  const viewPortWidth = window.innerWidth

  const urlParamsMetadata = pickAll(
    [
      'checkIn',
      'checkOut',
      'currency',
      'userIp',
      'anonymousId',
      'deviceType',
      'locale',
      'providerCode',
      'rooms',
      'hotelId',
      'userCountry',
      'providerRateType',
      'redirectId',
      'userAgent'
    ],
    urlParams
  )

  return {
    ...urlParamsMetadata,
    searchId,
    hotelPagesSearchId,
    userId,
    viewPortHeight,
    viewPortWidth
  }
}

export const getEventProperties = (event: TrackEventNewType) => {
  const {urlParams} = event

  const meta = getMetadata(urlParams)
  const eventProps = pickAll(
    ['category', 'space', 'action', 'label', 'value', 'context'],
    event
  )

  return {
    ...eventProps,
    meta
  }
}

export const getPageSpace = (pathname: string) => {
  // eslint-disable-next-line default-case
  switch (pathname) {
    case ROOM_SELECTION_PAGE_PATH:
      return EventSpace.ROOM_SELECTION_PAGE

    case PAYMENT_PAGE_PATH:
      return EventSpace.PAYMENT_PAGE

    case CONFIRMATION_PAGE_PATH:
      return EventSpace.BOOKING_CONFIRMATION_PAGE
  }
}

export const getPayOfferLabel = (offers: Offer[]) => {
  const payAtPropertyOffer = offers?.find(propEq('payAtProperty', true))
  const payNowOffer = offers?.find(propEq('payAtProperty', false))

  if (!payAtPropertyOffer && !payNowOffer) return 'missing both offers'
  if (!payAtPropertyOffer) return 'missing pay at property offer'
  if (!payNowOffer) return 'missing pay now offer'
  return null
}

export const getOfferContext = ({
  roomName,
  offer,
  roomPosition,
  roomId,
  offerPosition,
  raaAnchorPriceBreakdown,
  cheapestOffer,
  numberOfImages,
  roomAmenities,
  roomDescription
}: GetOfferContextPropsType): OfferContextType => {
  if (!offer) return null

  const {
    id,
    refundable,
    services,
    prices,
    payAtProperty,
    providerRateType,
    providerCode
  } = offer

  const offerParams = {
    offerId: id,
    refundable,
    services,
    payAtProperty,
    providerRateType,
    providerCode
  }

  const privateDealStatus = isPrivateDeal(offer)

  const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(prices)
  const price = getDisplayPrice(prices)

  const anchorPrice = raaAnchorPriceBreakdown
    ? getAnchorPriceNightlyTotal(raaAnchorPriceBreakdown)
    : null

  const cheapestOfferNightlyDisplayTotalPrice =
    cheapestOffer && getNightlyDisplayTotalPrice(cheapestOffer.prices)

  const isAnchorPriceShown = shouldAnchorPriceBeVisible(
    anchorPrice,
    displayPricePerRoomPerNight,
    cheapestOfferNightlyDisplayTotalPrice
  )

  const cancellationDeadline = getCancellationDeadline(
    offer?.cancellationPenalties
  )

  const otherParams = {
    roomName,
    isPrivateDeal: privateDealStatus,
    displayPricePerRoomPerNight,
    roomPosition,
    offerPosition,
    anchorPrice: isAnchorPriceShown ? anchorPrice : null,
    isAnchorPriceShown,
    cancellationDeadline,
    roomId,
    totalStayPrice: getTotalPrice(price),
    nightlyTaxes: price.chargeable.nightlyTaxes,
    nightlyBasePrice: price.chargeable.nightlyBase,
    nightlyLocalTaxes: getHotelNightlyFees(price),
    numberOfImages,
    roomAmenities,
    roomDescription
  }

  return {
    ...offerParams,
    ...otherParams
  }
}

// This context should only be added to page impression events
// the data pipeline will assume that this list is the sorted list shown in the Room Selection Page
// and take the top most offer and treat it as the offer that was selected by the user in the Search Results Page
export const getOffersListContext = ({
  rooms,
  raaAnchorPriceBreakdown
}: GetOffersListContextPropsType): OffersListContextType => {
  const nestedOffers = rooms.map((room, roomIndex) => {
    const roomName = room.name
    const roomId = room.id
    const roomMasterId = room.masterId

    const {offers} = room

    return offers.map((offer, offerIndex) => {
      // Added for sapi4eva-match-clicked-offer A/B test. Clean up when finished.
      const {isClicked, matchType} = offer

      return {
        // Apply roomMasterId specifically to OffersListContext so that analytics team can test room squashing experiment
        roomMasterId,
        isClicked,
        matchType,
        ...getOfferContext({
          roomName,
          roomId,
          offer,
          roomPosition: roomIndex + 1,
          offerPosition: offerIndex + 1,
          raaAnchorPriceBreakdown,
          cheapestOffer: rooms[0].offers[0],
          numberOfImages: room.images?.length,
          roomDescription: room.description,
          roomAmenities: room.amenities
        })
      }
    })
  })

  return flatten(nestedOffers)
}

export const getPayOffersListContext = ({
  room,
  offers,
  roomPosition,
  offerPosition,
  raaAnchorPriceBreakdown,
  cheapestOffer
}: GetPayOffersListContextPropsType): OffersListContextType => {
  return offers.map(offer => ({
    // Apply roomMasterId specifically to OffersListContext so that analytics team can test room squashing experiment
    roomMasterId: room.masterId,
    ...getOfferContext({
      roomName: room.name,
      roomId: room.id,
      offer,
      roomPosition,
      offerPosition,
      raaAnchorPriceBreakdown,
      cheapestOffer,
      numberOfImages: room.images?.length,
      roomDescription: room.description,
      roomAmenities: room.amenities
    })
  }))
}

export const getComparisonOfferContext = ({
  raaOffer,
  offerPosition,
  raaAnchorPriceBreakdown
}: GetComparisonOfferContextPropsType): ComparisonOfferContextType => {
  const {
    id,
    hasFreeCancellation,
    meals,
    canPayLater,
    proxyProviderCode,
    roomName,
    prices
  } = raaOffer

  const offerParams = {
    offerId: id,
    refundable: hasFreeCancellation,
    services: meals,
    providerCode: proxyProviderCode,
    roomName,
    canPayLater
  }

  const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(prices)

  const price = getDisplayPrice(prices)
  const totalPrice = getTotalPrice(price)

  const anchorPrice = raaAnchorPriceBreakdown
    ? getAnchorPriceNightlyTotal(raaAnchorPriceBreakdown)
    : undefined

  const isAnchorPriceShown = shouldComparisonOfferAnchorPriceBeVisible(
    anchorPrice,
    displayPricePerRoomPerNight
  )

  const otherParams = {
    offerPosition,
    displayPricePerRoomPerNight,
    anchorPrice: isAnchorPriceShown ? anchorPrice : undefined,
    isAnchorPriceShown,
    totalStayPrice: totalPrice,
    nightlyTaxes: price.chargeable.nightlyTaxes,
    nightlyBasePrice: price.chargeable.nightlyBase,
    nightlyLocalTaxes: getHotelNightlyFees(price)
  }

  const context: ComparisonOfferContextType = {
    ...offerParams,
    ...otherParams
  }

  return context
}

export const getComparisonOffersListContext = ({
  raaOffers,
  raaAnchorPriceBreakdown
}: GetComparisonOfferListContextPropsType): ComparisonOffersListContextType =>
  raaOffers.map((raaOffer, offerIndex) =>
    getComparisonOfferContext({
      raaOffer,
      offerPosition: offerIndex + 1,
      raaAnchorPriceBreakdown
    })
  )

export const getPriceContext = ({
  offer,
  raaAnchorPriceBreakdown,
  roomsConfig,
  cheapestOffer
}: GetPriceContextPropsType): PriceContextType => {
  if (!offer) return null

  const offerParams = pipe(
    pickAll(['id']),
    renamePropName('id', 'offerId')
  )(offer)

  const {numberOfRooms} = roomsConfig

  const price = getDisplayPrice(offer.prices)

  const currency = price.currencyCode
  const basePricePerRoomPerNight = price.chargeable.base
  const taxesPerRoomPerNight = price.chargeable.taxes
  const displayPricePerRoomPerNight = getNightlyDisplayTotalPrice(offer.prices)
  const totalUpFront = getTotalUpFrontPrice(price, offer.payAtProperty)
  const totalPrice = getTotalPrice(price)

  const resortFeeDueAtProperty = getHotelFeesResortFee(price)
  const taxesDueAtProperty = getHotelFeesTax(price)
  const otherDueAtProperty = getHotelFeesOther(price)
  const totalDueAtProperty = getTotalAtProperty(price, offer.payAtProperty)

  const hasCurrencyConversion = isThereACurrencyConversion(offer.prices)
  const chargeableCurrencyPrice = getChargeableCurrencyPrice(offer.prices)
  const chargeableCurrency = hasCurrencyConversion
    ? chargeableCurrencyPrice.currencyCode
    : null
  const chargeableTotalPrice = hasCurrencyConversion
    ? getTotalPrice(chargeableCurrencyPrice)
    : null

  const priceParams = {
    currency,
    basePricePerRoomPerNight,
    taxesPerRoomPerNight,
    displayPricePerRoomPerNight,
    totalUpFront,
    totalPrice,
    resortFeeDueAtProperty,
    taxesDueAtProperty,
    otherDueAtProperty,
    totalDueAtProperty,
    chargeableCurrency,
    chargeableTotalPrice
  }

  const anchorPrice = raaAnchorPriceBreakdown
    ? getAnchorPriceNightlyTotal(raaAnchorPriceBreakdown)
    : null

  const cheapestOfferNightlyDisplayTotalPrice =
    cheapestOffer && getNightlyDisplayTotalPrice(cheapestOffer.prices)

  const isAnchorPriceShown = shouldAnchorPriceBeVisible(
    anchorPrice,
    displayPricePerRoomPerNight,
    cheapestOfferNightlyDisplayTotalPrice
  )

  const savingPercentage = isAnchorPriceShown
    ? getSavingPercentage(anchorPrice, displayPricePerRoomPerNight)
    : null

  const savingTotal = isAnchorPriceShown
    ? getTotalSaving(raaAnchorPriceBreakdown, price, numberOfRooms)
    : null

  const anchorPriceParams = {
    anchorPrice: isAnchorPriceShown ? anchorPrice : null,
    isAnchorPriceShown,
    savingPercentage,
    savingTotal
  }

  return mergeAll([offerParams, priceParams, anchorPriceParams])
}
